/**
 * Popover de retouches IA ad-hoc (Phase 5).
 *
 * Affiche un bouton flottant « ✨ Retoucher » à proximité d'une sélection
 * faite dans l'iframe de prévisualisation. Au clic, ouvre un mini-prompt
 * avec un champ libre et des boutons d'instructions rapides.
 *
 * Ce composant est PUREMENT UI : il reçoit la sélection courante, la position
 * du bouton et un callback `onRevise(instruction)`. Le réseau (appel OpenAI)
 * et le postMessage de remplacement sont la responsabilité du parent.
 */
import { useState, useEffect, useRef } from 'react'
import { Loader2, Sparkles, X } from 'lucide-react'
import { QUICK_INSTRUCTIONS } from './quickInstructions'
import type { TranslateFn } from './context'

export interface RevisionPopoverProps {
  /** Texte sélectionné dans l'iframe (filtré, non vide). */
  selectionText: string
  /** Position en coordonnées viewport du parent où ancrer le popover. */
  anchor: { x: number; y: number }
  /** Indique qu'une retouche est en cours. */
  busy: boolean
  /** Erreur à afficher (vide / undefined pour rien). */
  error?: string
  /** Appelé quand l'utilisateur valide une instruction. */
  onRevise: (instruction: string) => void
  /** Appelé quand l'utilisateur ferme le popover sans rien faire. */
  onCancel: () => void
  /** Traduction fournie par l'app (voir context.ts). */
  t: TranslateFn
}

export default function RevisionPopover({
  selectionText,
  anchor,
  busy,
  error,
  onRevise,
  onCancel,
  t,
}: RevisionPopoverProps) {
  // 'collapsed' = juste le bouton ✨ ; 'expanded' = mini-prompt visible.
  const [mode, setMode] = useState<'collapsed' | 'expanded'>('collapsed')
  const [instruction, setInstruction] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Quand la sélection change, on revient en mode bouton (la précédente
  // intention n'a plus de sens).
  useEffect(() => {
    setMode('collapsed')
    setInstruction('')
  }, [selectionText])

  // Quand on passe en mode expanded, on focus l'input.
  useEffect(() => {
    if (mode === 'expanded') {
      inputRef.current?.focus()
    }
  }, [mode])

  const trigger = (text: string) => {
    const trimmed = text.trim()
    if (!trimmed) return
    onRevise(trimmed)
  }

  // Borne la position pour ne pas sortir de l'écran (le popover fait ~360x180
  // en expanded). Best-effort : on garde au moins 8 px de marge.
  const left = Math.max(8, Math.min(anchor.x, window.innerWidth - 380))
  const top = Math.max(8, Math.min(anchor.y, window.innerHeight - 220))

  const style: React.CSSProperties = {
    position: 'fixed',
    left,
    top,
    zIndex: 10001,
  }

  if (mode === 'collapsed') {
    return (
      <button
        type="button"
        className="revision-trigger"
        style={style}
        onClick={() => setMode('expanded')}
        disabled={busy}
        data-testid="revision-trigger"
        title={`${t('revision.trigger')} : "${selectionText.slice(0, 60)}${selectionText.length > 60 ? '…' : ''}"`}
      >
        <Sparkles className="w-3.5 h-3.5" />
        {t('revision.trigger')}
      </button>
    )
  }

  return (
    <div className="revision-popover" style={style} role="dialog" aria-label={t('revision.title')} data-testid="revision-popover">
      <div className="revision-popover-head">
        <span className="revision-popover-title">
          <Sparkles className="w-3.5 h-3.5" /> {t('revision.title')}
        </span>
        <button type="button" onClick={onCancel} className="revision-popover-close" aria-label={t('revision.cancel')} data-testid="revision-cancel">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <p className="revision-popover-selection" title={selectionText}>
        « {selectionText.slice(0, 90)}{selectionText.length > 90 ? '…' : ''} »
      </p>
      <div className="revision-popover-presets">
        {QUICK_INSTRUCTIONS.map((p) => (
          <button
            key={p.id}
            type="button"
            disabled={busy}
            onClick={() => trigger(p.instruction)}
            className="revision-preset"
            data-testid={`revision-preset-${p.id}`}
          >
            {t(`revision.preset.${p.id}`)}
          </button>
        ))}
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          trigger(instruction)
        }}
        className="revision-popover-form"
      >
        <input
          ref={inputRef}
          type="text"
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          placeholder={t('revision.placeholder')}
          disabled={busy}
          className="revision-popover-input"
          data-testid="revision-input"
        />
        <button
          type="submit"
          disabled={busy || !instruction.trim()}
          className="revision-popover-submit"
          data-testid="revision-submit"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 spin" /> : t('revision.submit')}
        </button>
      </form>
      {error && <p className="revision-popover-error" data-testid="revision-error">{error}</p>}
    </div>
  )
}
